import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Button, Alert, AlertType, GenericError, ProgressBar } from '@egw/components'
import { Logger } from '@egw/logger'
import { WEALTH_ROUTES } from '@egw/constants'
import { type FC } from 'react'
import { useGetEnrollmentMultiConfirmInformationData, useSubmitEnrollmentMultiConfirmInformationData } from '@egw/api'
import { useNavigate } from 'react-router'
import { useTranslation } from '@egw/i18n'
import { formatInputToCurrency, formatPhoneNumber } from '@egw/utils'
import { PageHeader } from '../../../components'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { ConfirmationSection } from '../../../components/confirmation-section/confirmation-section'
import { ROUTES as MULTI_ROUTES } from '../../../constants/routes/routes-multi'
import { ROUTES as GENERIC_ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - Multi - ConfirmInformation')

const getBackPath = (route: string): string => {
  return `${WEALTH_ROUTES.ENROLLMENT.path}/${route}?edit=true`
}
export const ConfirmInformationPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentMultiConfirmInformationData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiConfirmInformationData()

  const progress = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.progress : undefined

  const sections = [
    {
      title: 'Personal Info',
      fields: [
        {
          value: 'Legal first name',
          rightSide: data?.enrollmentData?.firstName,
          testId: 'firstName',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.MULTI_GET_STARTED.path))
          },
          isPII: true,
        },
        {
          value: 'Legal last name',
          rightSide: data?.enrollmentData?.lastName,
          testId: 'lastName',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.MULTI_GET_STARTED.path))
          },
          isPII: true,
        },
        {
          value: 'Phone',
          rightSide: formatPhoneNumber(data?.enrollmentData?.phoneNumber),
          testId: 'phoneNumber',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.MULTI_GET_STARTED.path))
          },
          isPII: true,
        },
        {
          value: 'Email',
          rightSide: data?.enrollmentData?.email,
          testId: 'email',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.MULTI_GET_STARTED.path))
          },
          isPII: true,
        },
        {
          value: 'Address',
          rightSide: (
            <p className="flex flex-col justify-end items-end">
              <span>
                {data?.enrollmentData?.addressLine1}
                {data?.enrollmentData?.addressLine2 ? `, ${data.enrollmentData.addressLine2}` : ''}
              </span>
              <span>
                {data?.enrollmentData?.addressCity}, {data?.enrollmentData?.addressRegionCode}{' '}
                {data?.enrollmentData?.addressPostalCode}
              </span>
            </p>
          ),
          testId: 'address',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.HOME_ADDRESS.path))
          },
          isPII: true,
        },
        {
          value: 'DOB',
          rightSide: data?.enrollmentData?.dateOfBirth,
          testId: 'dateOfBirth',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.VERIFY_IDENTITY.path))
          },
          isPII: true,
        },
        {
          value: 'SSN/ITIN',
          rightSide: data?.enrollmentData?.maskedSsn,
          testId: 'ssn',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.VERIFY_IDENTITY.path))
          },
          isPII: true,
        },
      ],
    },
    {
      title: 'Employment',
      fields: [
        {
          value: 'Employment status',
          rightSide: t(`multi.confirm_information.employment_status.${data?.enrollmentData?.employmentStatus ?? ''}`),
          testId: 'employmentStatus',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.EMPLOYMENT.path))
          },
        },
        ...(data?.enrollmentData?.occupation
          ? [
              {
                value: 'Occupation',
                rightSide: t(`multi.confirm_information.occupation.${data.enrollmentData.occupation}`),
                testId: 'occupation',
                onEdit: () => {
                  void navigate(getBackPath(MULTI_ROUTES.EMPLOYMENT.path))
                },
              },
            ]
          : []),
        {
          value: 'Brokerage affiliation',
          rightSide: data?.enrollmentData?.isBrokerDealer ? 'Yes' : 'No',
          testId: 'brokerageAffiliation',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.SUITABILITY.path))
          },
        },
        {
          value: 'Executive or shareholder',
          rightSide: data?.enrollmentData?.isDirectorPolicyMakerOfCompany ? 'Yes' : 'No',
          testId: 'isDirectorPolicyMakerOfCompany',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.SUITABILITY.path))
          },
        },
        {
          value: 'Politically exposed person (PEP)',
          rightSide: data?.enrollmentData?.investmentProfile?.isPoliticallyExposedPerson ? 'Yes' : 'No',
          testId: 'pep',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.SUITABILITY.path))
          },
        },
        {
          value: 'Immediate family member or close associate of politically exposed person (PEP)',
          rightSide: data?.enrollmentData?.investmentProfile?.hasPoliticallyExposedImmediateFamilyMembers
            ? 'Yes'
            : 'No',
          testId: 'family_member_pep',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.SUITABILITY.path))
          },
          isPII: true,
        },
      ],
    },
    {
      title: 'Finances',
      fields: [
        {
          value: 'Marital status',
          rightSide: data?.enrollmentData?.investmentProfile?.maritalStatus
            ? t(`marital_status.${data.enrollmentData.investmentProfile.maritalStatus}`)
            : t('common.not_specified'),
          testId: 'maritalStatus',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.FINANCES.path))
          },
        },
        {
          value: 'Tax filing status',
          rightSide: t(
            `multi.confirm_information.filing-status.tax_filing_status.${data?.enrollmentData?.investmentProfile?.taxFilingStatus ?? ''}`,
          ),
          testId: 'filingStatus',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.FINANCES.path))
          },
        },
        {
          value: 'Annual income',
          rightSide: formatInputToCurrency(data?.enrollmentData?.personalAnnualGrossIncome.toString() ?? '', true),
          testId: 'annualIncome',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.FINANCES.path))
          },
          isPII: true,
        },
        {
          value: 'Net worth',
          rightSide: t(
            `multi.confirm_information.personal_net_worth_range.${data?.enrollmentData?.personalNetWorthRange ?? ''}`,
          ),
          testId: 'netWorth',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.FINANCES.path))
          },
          isPII: true,
        },
        {
          value: 'Investment objective',
          rightSide: t(
            `multi.investment-goals.investment_objective.${data?.enrollmentData?.investmentProfile?.objective ?? ''}`,
          ),
          testId: 'investmentObjective',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.INVESTMENT_GOALS.path))
          },
        },
        {
          value: 'Investment experience',
          rightSide: t(
            `multi.investment-goals.investment_experience.${data?.enrollmentData?.investmentProfile?.experience ?? ''}`,
          ),
          testId: 'investmentExperience',
          onEdit: () => {
            void navigate(getBackPath(MULTI_ROUTES.INVESTMENT_GOALS.path))
          },
        },
      ],
    },
  ]

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = async (): Promise<void> => {
    mutate({
      proceedConfirmInfo: true,
      triggerRiskCheck: true,
    })
      .then((response) => {
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          void navigate(response.path)
        } else if (!response?.path) {
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.ERROR.path}`)
        }
      })
      .catch((e) => {
        log.error('Error submitting form', e)
      })
  }
  // AUTO GENERATED FORM HANDLERS END

  return (
    <EnrollmentMultiLayout title={t('multi.confirm_information.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {/* AUTO GENERATED COMPONENTS START*/}
          <div className="w-full flex-1 flex flex-col justify-start">
            <PageHeader title={t('multi.confirm_information.title')} />
            <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />
            <p className="typo-primary-base-regular text-text-meta mb-xl">
              {t('multi.confirm_information.description')}
            </p>
            {sections.map((e) => {
              return (
                <ConfirmationSection
                  title={e.title}
                  fields={e.fields}
                  key={e.title.toLowerCase().replaceAll(' ', '-')}
                />
              )
            })}
            <div className="flex-1 md:hidden" />

            {/* AUTO GENERATED COMPONENTS */}

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            <Button
              type="button"
              className="w-full md:w-min mt-xl"
              isLoading={isSubmitting}
              testId="primaryAction_button"
              onClick={() => void onSubmit()}
            >
              {t('multi.confirm_information.continue')}
            </Button>
          </div>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
